class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    if (!this.mainDetailsToggle) return;

    this.content = this.mainDetailsToggle.querySelector('summary')?.nextElementSibling;
    this.closeTimer = null;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 120);
  }

  onToggle() {
    if (!this.content) return;
    if (!this.animations) this.animations = this.content.getAnimations?.() || [];

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play?.());
    } else {
      this.animations.forEach((animation) => animation.cancel?.());
    }
  }

  close() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
    if (!this.mainDetailsToggle) return;
    this.mainDetailsToggle.removeAttribute('open');
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  disconnectedCallback() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }
}

if (!customElements.get('details-disclosure')) {
  customElements.define('details-disclosure', DetailsDisclosure);
}

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    if (!this.mainDetailsToggle) return;

    this.header =
      document.querySelector('.header-wrapper') ||
      document.querySelector('.fp-header-row') ||
      document.querySelector('.app-che-header');

    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.closeSiblings = this.closeSiblings.bind(this);

    this.nestedTimers = new Map();

    // Hover-open is mouse-only: a tap also fires enter events, and the native
    // summary click that follows would immediately toggle the menu shut again.
    this.mainDetailsToggle.addEventListener('pointerenter', this.onEnter);
    this.mainDetailsToggle.addEventListener('pointerleave', this.onLeave);

    // Close other menus before native toggle (toggle event can be async)
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) {
      summary.addEventListener('pointerdown', this.closeSiblings);
      summary.addEventListener('focus', this.closeSiblings);
    }

    // Nested flyouts — hover open/close with the same delay pattern
    this.querySelectorAll('details.fp-desktop-nav__nested').forEach((nested) => {
      nested.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.nestedTimers.get(nested));
        clearTimeout(this.closeTimer);
        nested.setAttribute('open', '');
      });
      nested.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        const nestedTimer = setTimeout(() => nested.removeAttribute('open'), 160);
        this.nestedTimers.set(nested, nestedTimer);
      });
    });

    // Keep parent open while interacting with submenu links
    const submenu = this.mainDetailsToggle.querySelector('.fp-desktop-nav__submenu');
    if (submenu) {
      submenu.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
    }
  }

  close() {
    if (this.nestedTimers) {
      this.nestedTimers.forEach((timer) => clearTimeout(timer));
      this.nestedTimers.clear();
    }
    this.querySelectorAll('details.fp-desktop-nav__nested[open]').forEach((nested) => {
      nested.removeAttribute('open');
    });
    super.close();
  }

  disconnectedCallback() {
    if (this.nestedTimers) {
      this.nestedTimers.forEach((timer) => clearTimeout(timer));
      this.nestedTimers.clear();
    }
    super.disconnectedCallback();
  }

  closeSiblings() {
    document.querySelectorAll('header-menu > details[open]').forEach((details) => {
      if (details !== this.mainDetailsToggle) {
        details.removeAttribute('open');
        const summary = details.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      }
    });
  }

  onEnter(event) {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.closeTimer);
    this.openMenu();
  }

  onLeave(event) {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), 180);
  }

  openMenu() {
    this.closeSiblings();
    this.mainDetailsToggle.setAttribute('open', '');
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');
  }

  onToggle() {
    if (!this.mainDetailsToggle) return;
    if (this.mainDetailsToggle.open) {
      this.closeSiblings();
    }

    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', this.mainDetailsToggle.open ? 'true' : 'false');
    }

    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

// Stabilize header search <details> — don't snap shut while using the panel
(() => {
  /* The page behind the search sheet must not scroll. The scrollbar width is
     reserved as padding so the layout doesn't jump when it disappears.
     gk-scroll-motion.js stops Lenis whenever body overflow is hidden. */
  /* Non-passive, so it is only attached while the sheet is open. Wheel and
     touch-drag inside the sheet still scroll it; anywhere else they're eaten,
     which covers browsers (older iOS Safari) that ignore overflow:hidden. */
  const blockOutside = (event) => {
    if (event.target.closest && event.target.closest('.fp-header-search__panel')) return;
    event.preventDefault();
  };

  const setScrollLock = (locked) => {
    const root = document.documentElement;
    if (locked === root.classList.contains('gk-search-lock')) return;
    if (locked) {
      const gutter = window.innerWidth - root.clientWidth;
      if (gutter > 0) document.body.style.paddingRight = gutter + 'px';
      root.classList.add('gk-search-lock');
      document.body.classList.add('gk-search-lock');
      document.addEventListener('wheel', blockOutside, { passive: false });
      document.addEventListener('touchmove', blockOutside, { passive: false });
    } else {
      root.classList.remove('gk-search-lock');
      document.body.classList.remove('gk-search-lock');
      document.body.style.paddingRight = '';
      document.removeEventListener('wheel', blockOutside);
      document.removeEventListener('touchmove', blockOutside);
    }
  };

  window.addEventListener('pageshow', () => {
    if (!document.querySelector('details.fp-header-search[open]')) setScrollLock(false);
  });

  const bindSearchDetails = (searchDetails) => {
    if (!searchDetails || searchDetails.dataset.fpSearchBound === 'true') return;
    const summary = searchDetails.querySelector('summary');
    if (!summary) return;

    searchDetails.dataset.fpSearchBound = 'true';

    let closeTimer = null;
    const panel = searchDetails.querySelector('.fp-header-search__panel');

    const keepOpen = () => clearTimeout(closeTimer);
    const scheduleClose = (event) => {
      if (searchDetails.contains(event.relatedTarget)) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        if (!searchDetails.matches(':hover') && !searchDetails.contains(document.activeElement)) {
          searchDetails.removeAttribute('open');
        }
      }, 200);
    };

    searchDetails.addEventListener('mouseenter', keepOpen);
    searchDetails.addEventListener('mouseleave', scheduleClose);
    if (panel) {
      panel.addEventListener('mouseenter', keepOpen);
      panel.addEventListener('focusin', keepOpen);
    }

    /* The panel is position:fixed full-width; it docks under whatever part of
       the sticky header is currently on screen. */
    const placePanel = () => {
      const header = searchDetails.closest('.shopify-section, header') || document.querySelector('.section-header');
      const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      searchDetails.style.setProperty('--gk-search-top', bottom + 'px');
    };

    const onViewportChange = () => {
      if (searchDetails.open) placePanel();
    };

    searchDetails.addEventListener('toggle', () => {
      setScrollLock(searchDetails.open);
      if (!searchDetails.open) {
        window.removeEventListener('resize', onViewportChange);
        return;
      }
      window.addEventListener('resize', onViewportChange, { passive: true });
      placePanel();
      const input = searchDetails.querySelector('input[type="search"]');
      if (input) requestAnimationFrame(() => input.focus({ preventScroll: true }));
    });

    const closeBtn = searchDetails.querySelector('[data-gk-search-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        searchDetails.removeAttribute('open');
        summary.focus();
      });
    }
  };

  const initHeaderSearch = () => {
    document.querySelectorAll('details.fp-header-search').forEach(bindSearchDetails);
  };

  if (!window.__fpHeaderSearchDocBound) {
    window.__fpHeaderSearchDocBound = true;

    document.addEventListener('click', (event) => {
      document.querySelectorAll('details.fp-header-search[open]').forEach((searchDetails) => {
        /* A click on the <details> itself is the ::before backdrop. */
        if (searchDetails.contains(event.target) && event.target !== searchDetails) return;
        searchDetails.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('details.fp-header-search[open]').forEach((searchDetails) => {
        searchDetails.removeAttribute('open');
        const summary = searchDetails.querySelector('summary');
        if (summary) summary.focus();
      });
    });

    document.addEventListener('shopify:section:load', initHeaderSearch);
  }

  initHeaderSearch();
})();
